import React, { useEffect, useState, useRef, forwardRef } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  MenuItem,
  Divider,
  FormControlLabel,
  RadioGroup,
  FormHelperText,
  Radio,
  Autocomplete,
  createFilterOptions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "../../App.css";
import BookingGetters from "../../store/onlineBooking/getters";
import { BookingApi } from "../../API/Apis/BookingApi";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  it as itLocale,
  es as esLocale,
  fr as frLocale,
  de as deLocale,
} from "date-fns/locale";
import { itIT, esES, frFR, deDE } from "@mui/x-date-pickers/locales";
import PhoneInput from "react-phone-input-2";
import WebCheckInLayout from "./WebCheckInLayout";
import WebCheckInPopUp from "./WebCheckInPopUp";
import { calculateNestedProgress } from "../../helpers/calculateNestedProgress";
import i18n from "../../config/i18n";
// import React, { forwardRef } from "react";
// import PhoneInput from "react-phone-input-2";
import AppCountrySelect from "../../components/Layouts/AppCountrySelect";

type Props = {
  reservationDetails: any;
  driverNumber: number;
  documentTypes: any;
  currentCustomerDetails: any;
  onBack: () => void;
  onNext?: (data: any) => void;
  allDriverDetails: any;
  setIsLoading: any;
  isSmallScreen: boolean;
  stepGroups?: any;
  rentalAnswer: any;
  progressCalculator?: (
    currentSection: number,
    totalSections: number
  ) => number;
  isShareboxEnabled?: boolean;
  onWideChange?: (isWide: boolean) => void;
};

type FormData = {
  // Section 1
  firstName: string;
  lastName: string;
  telephone: string;
  email: string;

  // Section 2
  streetName: string;
  streetNumber: string;
  city: string;
  state: string;
  zip: string;
  country: string;

  // Section 3
  dateOfBirth: string;
  birthPlace: string;
  birthProvince: string;
  birthNation: string;

  // Section 4
  sex: string;
  ssnNumber: string;
  vatNumber: string;
  // Section 5
  document: string;
  documentNumber: string;
  licenseType: string;
  releaseDate: string;
  documentExpiryDate: string;
  issueBy: string;

  // Section 6
  document1: string;
  documentNumb1: string;
  releaseDate1: string;
  expiryDate1: string;
  issueBy1: string;
};

// eslint-disable-next-line react/display-name
const RHFPhoneInput = forwardRef(({ value, onChange, ...props }: any, ref) => (
  <PhoneInput
    {...props}
    value={value}
    onChange={onChange}
    inputProps={{ ref }}
    containerStyle={{
      marginBottom: "15px",
    }}
    inputStyle={{
      width: "100%",
      height: "56px",
      borderRadius: "8px",
      fontSize: "14px",
      borderColor: "#19344FCC",
      borderWidth: "2px",
      boxShadow: "none",
    }}
    buttonStyle={{
      border: "none",
    }}
    specialLabel=""
  />
));

export default function DriverDetails({
  reservationDetails,
  driverNumber,
  documentTypes,
  currentCustomerDetails,
  onBack,
  onNext,
  allDriverDetails,
  setIsLoading,
  isSmallScreen,
  stepGroups,
  rentalAnswer,
  progressCalculator,
  isShareboxEnabled,
  onWideChange,
}: Props) {
  if (!reservationDetails) return null;
  const { t: translate } = useTranslation();
  const lang = i18n.language;
  const dateLocale =
    lang === "it"
      ? itLocale
      : lang === "es"
      ? esLocale
      : lang === "fr"
      ? frLocale
      : lang === "de"
      ? deLocale
      : undefined;
  const muiLocaleText =
    lang === "it"
      ? itIT.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "es"
      ? esES.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "fr"
      ? frFR.components.MuiLocalizationProvider.defaultProps.localeText
      : lang === "de"
      ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
      : undefined;
  const [currentSection, setCurrentSection] = useState(1);
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [birthCities, setBirthCities] = useState<string[]>([]);
  const [birthStates, setBirthStates] = useState<string[]>([]);
  const [zipCode, setZipCode] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [isBlacklisted, setIsBlacklisted] = useState<boolean>(false);
  const [isBlacklistedText, setIsBlacklistedText] = useState<string>("");
  const [driverLicenseExpiredText, setDriverLicenseExpiredText] =
    useState<string>("");
  const [selectedProfile, setSelectedProfile] = useState<"new" | "existing">(
    "new"
  );
  const [existingDriverData, setExistingDriverData] = useState<any>(null);

  const appSettings = BookingGetters.getAppSettings();
  const enableUppercaseInputField = appSettings?.enableUppercaseInputField;
  const webCheckinHeaderBgColor = appSettings?.webCheckinHeaderBgColor;
  const country = appSettings?.config?.country;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const totalSections = 7;
  const nestedProgress = progressCalculator
    ? progressCalculator(currentSection, totalSections)
    : calculateNestedProgress({
        currentPage: "DriverDetails",
        currentSection,
        totalSections,
        stepGroups,
        currentDriver: driverNumber,
      });

  const webCheckInRadioGroup = {
    marginBottom: isSmallScreen ? "15px" : "30px",
  };

  const isMountedRef = useRef(true);
  const ssnDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await BookingApi.getAllCountries(country);
        if (isMountedRef.current) {
          setCountries(response.data);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, [country]);

  const documentTypeMap: Record<string, string> = {
    ID_CARD: "CARTA D'IDENTITA'",
    PASSPORT: "PASSAPORTO",
    DIPLOMATIC_ID_CARD: "CARTA ID. DIPLOMATICA",
    ELECTRONIC_ID_CARD: "CARTA IDENTITA' ELETTRONICA",
    DIPLOMATIC_PASSPORT: "PASSAPORTO DIPLOMATICO",
    ORDINARY_PASSPORT: "PASSAPORTO ORDINARIO",
    SERVICE_PASSPORT: "PASSAPORTO DI SERVIZIO",
    CI: "CI",
    "CARTA DI IDENTITA'": "CARTA DI IDENTITA'",
    PASSAPORTO: "PASSAPORTO",
    "CARTA ID. DIPLOMATICA": "CARTA ID. DIPLOMATICA",
    "CARTA IDENTITA' ELETTRONICA": "CARTA IDENTITA' ELETTRONICA",
    "PASSAPORTO DIPLOMATICO": "PASSAPORTO DIPLOMATICO",
    "PASSAPORTO ORDINARIO": "PASSAPORTO ORDINARIO",
    "PASSAPORTO DI SERVIZIO": "PASSAPORTO DI SERVIZIO",
  };

  const buildDefaultValues = (driver: any): Partial<FormData> => {
    if (!driver) return {};
    return {
      firstName: driver.firstName || "",
      lastName: driver.lastName || "",
      telephone: driver.telephone || driver.phNum1 || driver.mobileNumber || "",
      email: driver.email || "",
      streetName: driver.streetName || "",
      streetNumber: driver.streetNumber || "",
      city: driver.city || "",
      state: driver.state || "",
      zip: driver.zip || "",
      country: driver.country || "",
      dateOfBirth: driver.dateOfBirth
        ? new Date(driver.dateOfBirth).toISOString().split("T")[0]
        : "",
      birthPlace: driver.birthPlace || "",
      birthProvince: driver.birthProvince || "",
      birthNation: driver.birthNation || "",
      sex:
        driver.sex?.toUpperCase() === "MALE"
          ? "MALE"
          : driver.sex?.toUpperCase() === "FEMALE"
          ? "FEMALE"
          : "",
      ssnNumber: driver.ssnNumber || "",
      vatNumber: driver.vatNumber || "",
      document: driver.document || "",
      documentNumber: driver.documentNumber || "",
      licenseType: driver.licenseType || "",
      releaseDate: driver.releaseDate
        ? new Date(driver.releaseDate).toISOString().split("T")[0]
        : "",
      documentExpiryDate: driver.documentExpiryDate
        ? new Date(driver.documentExpiryDate).toISOString().split("T")[0]
        : "",
      issueBy: driver.issueBy || driver.issuedBy || "",
      document1: documentTypeMap[driver.document1] || driver.document1 || "",
      documentNumb1: driver.documentNumb1 || "",
      issueBy1: driver.issueBy1 || "",
      releaseDate1: driver.releaseDate1
        ? new Date(driver.releaseDate1).toISOString().split("T")[0]
        : "",
      expiryDate1: driver.expiryDate1
        ? new Date(driver.expiryDate1).toISOString().split("T")[0]
        : "",
    };
  };

  const selectedDriver = allDriverDetails?.[driverNumber] || null;
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: buildDefaultValues(selectedDriver),
  });

  useEffect(() => {
    // if (allDriverDetails && allDriverDetails.length > 0) {
    //   const selectedDriver = allDriverDetails[driverNumber];
    setIsBlacklisted(false);
    if (selectedDriver) {
      reset({
        firstName: selectedDriver.firstName || "",
        lastName: selectedDriver.lastName || "",
        telephone: selectedDriver.telephone || "",
        email: selectedDriver.email || "",
        streetName: selectedDriver.streetName || "",
        streetNumber: selectedDriver.streetNumber || "",
        city: selectedDriver.city || "",
        state: selectedDriver.state || "",
        zip: selectedDriver.zip || "",
        country: selectedDriver.country || "",
        dateOfBirth: selectedDriver.dateOfBirth
          ? new Date(selectedDriver.dateOfBirth).toISOString().split("T")[0]
          : "",
        birthPlace: selectedDriver.birthPlace || "",
        birthProvince: selectedDriver.birthProvince || "",
        birthNation: selectedDriver.birthNation || "",
        sex:
          selectedDriver.sex?.toUpperCase() === "MALE"
            ? "MALE"
            : selectedDriver.sex?.toUpperCase() === "FEMALE"
            ? "FEMALE"
            : "",
        ssnNumber: selectedDriver.ssnNumber || "",
        vatNumber: selectedDriver.vatNumber || "",
        document: selectedDriver.document || "",
        documentNumber: selectedDriver.documentNumber || "",
        licenseType: selectedDriver.licenseType || "",
        releaseDate: selectedDriver.releaseDate
          ? new Date(selectedDriver.releaseDate).toISOString().split("T")[0]
          : "",
        documentExpiryDate: selectedDriver.documentExpiryDate
          ? new Date(selectedDriver.documentExpiryDate)
              .toISOString()
              .split("T")[0]
          : "",
        issueBy: selectedDriver.issueBy || selectedDriver.issuedBy || "",
        // document1: selectedDriver.document1 || "",
        // document1: documentTypeMap[selectedDriver.document1] || "",
        document1:
          documentTypeMap[selectedDriver.document1] ||
          selectedDriver.document1 ||
          "",
        documentNumb1: selectedDriver.documentNumb1 || "",
        issueBy1: selectedDriver.issueBy1 || "",
        releaseDate1: selectedDriver.releaseDate1
          ? new Date(selectedDriver.releaseDate1).toISOString().split("T")[0]
          : "",
        expiryDate1: selectedDriver.expiryDate1
          ? new Date(selectedDriver.expiryDate1).toISOString().split("T")[0]
          : "",
        // customerId: selectedDriver.customerId || "",
      });
    }
  }, [driverNumber, allDriverDetails, reset]);

  const selectedCountrys = useWatch({ control, name: "country" });
  const [isInvoiceAlreadyCreated, setIsInvoiceAlreadyCreated] =
    useState<boolean>(false);
  useEffect(() => {
    const checkInvoice = async () => {
      try {
        const italyAddress =
          selectedCountrys?.toUpperCase() === "ITALIA" ||
          selectedCountrys?.toUpperCase() === "ITALY";

        const vatNumberOrTaxCode = selectedDriver?.ssnNumber || "";
        if (
          !vatNumberOrTaxCode ||
          vatNumberOrTaxCode === "9999999999999999" ||
          !italyAddress
        )
          return;
        const response = await BookingApi.getExistingCustomerRAInv(
          vatNumberOrTaxCode
        );
        setIsInvoiceAlreadyCreated(response.data?.data?.isInvoiceOrRa === "Y");
      } catch (error) {
        console.error("Error checking invoice:", error);
        setIsInvoiceAlreadyCreated(false);
      }
    };
    checkInvoice();
  }, [selectedDriver?.ssnNumber, selectedCountrys]);

  const watchedSsnNumber = useWatch({ control, name: "ssnNumber" });

  // Check the current values from the form state
  const currentCountry = watch("country")?.toUpperCase();

  // Disable if NOT Italian
  const isNotItalian =
    currentCountry !== "ITALIA" && currentCountry !== "ITALY";

  useEffect(() => {
    onWideChange?.(
      currentSection === 7 && !isNotItalian && !!existingDriverData?.customers
    );
  }, [currentSection, isNotItalian, existingDriverData, onWideChange]);

  useEffect(() => {
    const restrictBlacklistCustomersFromBooking =
      appSettings?.restrictBlacklistCustomersFromBooking;
    const blacklistId = watchedSsnNumber || selectedDriver?.vatNumber;
    if (
      !blacklistId ||
      !restrictBlacklistCustomersFromBooking ||
      isNotItalian
    ) {
      return;
    }

    const checkBlacklist = async () => {
      try {
        const res = await BookingApi.checkIfCustomerIsBlacklisted(blacklistId);
        if (!isMountedRef.current) return;

        if (res?.data?.status === "success") {
          setIsBlacklisted(true);
        } else {
          setIsBlacklisted(false);
        }
      } catch (error) {
        console.error("Error checking blacklist status:", error);
        if (isMountedRef.current) {
          setIsBlacklisted(false);
        }
      }
    };

    checkBlacklist();
  }, [watchedSsnNumber, appSettings?.restrictBlacklistCustomersFromBooking]);

  useEffect(() => {
    const blacklistedTextData = data.find(
      (item) => item.key === "driverBlacklisted"
    );
    const licenceExpiredText = data.find(
      (item) => item.key === "DriverLicenseExpiredText"
    );
    setIsBlacklistedText(
      blacklistedTextData?.body || translate("driverBlacklisted")
    );
    setDriverLicenseExpiredText(
      licenceExpiredText?.body || translate("DriverLicenseExpiredText")
    );
  }, [lang, textResource, translate]);

  const handleContinue = async (data: FormData) => {
    if (reservationDetails?.dropOffDate) {
      const dropOffDate = new Date(reservationDetails.dropOffDate);
      dropOffDate.setHours(0, 0, 0, 0);

      // Section 5: validate driver license expiry date
      if (currentSection === 5 && data.documentExpiryDate) {
        const expiryDate = new Date(data.documentExpiryDate);
        expiryDate.setHours(0, 0, 0, 0);
        if (expiryDate <= dropOffDate) {
          setModalMessage(driverLicenseExpiredText);
          setOpenModal(true);
          return;
        }
      }

      // Section 6: validate ID/Passport expiry date
      if (currentSection === 6 && data.expiryDate1) {
        const expiryDate1 = new Date(data.expiryDate1);
        expiryDate1.setHours(0, 0, 0, 0);
        if (expiryDate1 <= dropOffDate) {
          setModalMessage(driverLicenseExpiredText);
          setOpenModal(true);
          return;
        }
      }
    }
    // At section 4 (Italian): only call API if driver fields have changed
    let freshDriverData = existingDriverData;
    if (currentSection === 4 && !isNotItalian) {
      const normDate = (v: any) => {
        if (v == null || v === "") return "";
        const s = String(v);
        return s.includes("T") ? s.split("T")[0] : s;
      };
      const norm = (v: any) => String(v ?? "").trim();
      const driverHasChanges =
        norm((data as any).firstName) !== norm(selectedDriver?.firstName) ||
        norm((data as any).lastName) !== norm(selectedDriver?.lastName) ||
        norm((data as any).telephone) !== norm(selectedDriver?.telephone) ||
        norm((data as any).email) !== norm(selectedDriver?.email) ||
        norm((data as any).streetName) !== norm(selectedDriver?.streetName) ||
        norm((data as any).streetNumber) !==
          norm(selectedDriver?.streetNumber) ||
        norm((data as any).city) !== norm(selectedDriver?.city) ||
        norm((data as any).state) !== norm(selectedDriver?.state) ||
        norm((data as any).zip) !== norm(selectedDriver?.zip) ||
        norm((data as any).country) !== norm(selectedDriver?.country) ||
        normDate((data as any).dateOfBirth) !==
          normDate(selectedDriver?.dateOfBirth) ||
        norm((data as any).birthPlace) !== norm(selectedDriver?.birthPlace) ||
        norm((data as any).birthProvince) !==
          norm(selectedDriver?.birthProvince) ||
        norm((data as any).birthNation) !== norm(selectedDriver?.birthNation) ||
        norm((data as any).sex) !== norm(selectedDriver?.sex) ||
        norm((data as any).ssnNumber) !== norm(selectedDriver?.ssnNumber);

      const ssnToUse =
        (data as any).ssnNumber || selectedDriver?.ssnNumber || "";
      if (ssnToUse && ssnToUse !== "9999999999999999" && driverHasChanges) {
        try {
          setIsLoading(true);
          const response = await BookingApi.getExistingCustomerRAInv(ssnToUse);
          const apiData = response?.data?.data;
          freshDriverData = apiData?.customers != null ? apiData : null;
          if (isMountedRef.current) setExistingDriverData(freshDriverData);
        } catch {
          freshDriverData = null;
          if (isMountedRef.current) setExistingDriverData(null);
        } finally {
          if (isMountedRef.current) setIsLoading(false);
        }
      } else if (!driverHasChanges) {
        // No changes — keep freshDriverData as null so section 7 is skipped
        freshDriverData = null;
        if (isMountedRef.current) setExistingDriverData(null);
      } else {
        freshDriverData = null;
        if (isMountedRef.current) setExistingDriverData(null);
      }
    }

    const hasExistingDriver = !isNotItalian && !!freshDriverData?.customers;

    if (currentSection < 6 || (currentSection === 6 && hasExistingDriver)) {
      setCurrentSection(currentSection + 1);
      return;
    }
    if (onNext) {
      const updatedDrivers = [...allDriverDetails];

      if (isBlacklisted) {
        setCurrentSection(1);
        setModalMessage(isBlacklistedText);
        setOpenModal(true);
        return;
      }

      const ec = freshDriverData?.customers;
      const profileData =
        selectedProfile === "existing" && ec
          ? {
              customerId: ec.customer_id || undefined,
              firstName: ec.firstName || "",
              lastName: ec.lastName || "",
              telephone: ec.telephone || ec.phoneNumber || ec.mobile || "",
              email: ec.email || ec.email_invoice || "",
              streetName: ec.street || "",
              streetNumber: ec.addressNumber || ec.n || "",
              zip: ec.postalCode || "",
              city: ec.city || "",
              state: ec.state || ec.province || "",
              country: ec.country || "",
              dateOfBirth: ec.dob || "",
              birthPlace: ec.birthplace || "",
              birthProvince: ec.birthProv || "",
              birthNation: ec.birthNation || "",
              ssnNumber: ec.codiceFiscale || ec.idNumber || "",
              document: ec.document || "",
              documentNumber: ec.documentNumb || "",
              licenseType: ec.licenseType || "",
              // releaseDate: ec.releaseDate || "",
              documentExpiryDate: ec.expiryDate || "",
              issueBy: ec.issuedBy || "",
              document1: ec.document1 || "",
              documentNumb1: ec.documentNumb1 || "",
              releaseDate1: ec.releaseDate1 || "",
              expiryDate1: ec.expiryDate1 || "",
              issueBy1: ec.issueBy1 || "",
            }
          : data;

      updatedDrivers[driverNumber] = {
        ...updatedDrivers[driverNumber],
        ...profileData,
        isDriver: "yes",
        // issueBy: "",
        customerType: currentCustomerDetails.customerType.toUpperCase(),
        // customerId: updatedDrivers[driverNumber]?.customerId || data.customerId,
      };
      try {
        setIsLoading(true);
        const currentDriver = updatedDrivers[driverNumber];

        // Archive old customer if user chose new profile and an existing record is present
        if (selectedProfile !== "existing" && ec) {
          const existingCustomerId = ec.customer_id || ec.code;
          if (existingCustomerId) {
            try {
              await BookingApi.archiveCustomer(existingCustomerId);
            } catch (error) {
              console.error("Failed to archive existing customer:", error);
            }
          }
        }

        const resolvedCustomerId = currentDriver?.customerId;
        const isCustomerSameAsDriver1 = Boolean(
          reservationDetails?.customerId &&
            reservationDetails?.driver1Id &&
            String(reservationDetails.customerId) ===
              String(reservationDetails.driver1Id)
        );
        // Foreigner check: if driver is non-Italian and has an existing record,
        // detect changes — if any changed, archive old and create new driver
        if (
          isNotItalian &&
          resolvedCustomerId &&
          !(driverNumber === 0 && rentalAnswer === "yes") &&
          !isCustomerSameAsDriver1
        ) {
          const fieldsToCompare = [
            "firstName",
            "lastName",
            "telephone",
            "email",
            "streetName",
            "streetNumber",
            "country",
            "city",
            "state",
            "zip",
            "dateOfBirth",
            "birthPlace",
            "birthProvince",
            "birthNation",
            "sex",
            "ssnNumber",
            "vatNumber",
            "document",
            "documentNumber",
            "licenseType",
            "releaseDate",
            "documentExpiryDate",
            "issueBy",
            "document1",
            "documentNumb1",
            "releaseDate1",
            "expiryDate1",
            "issueBy1",
          ];
          const originalDriver = selectedDriver || {};
          const dateFields = new Set([
            "dateOfBirth",
            "releaseDate",
            "documentExpiryDate",
            "releaseDate1",
            "expiryDate1",
          ]);
          const normalizeValue = (field: string, val: any): string => {
            if (val == null || val === "") return "";
            if (dateFields.has(field)) {
              const s = String(val);
              return s.includes("T") ? s.split("T")[0] : s;
            }
            return String(val);
          };
          const hasChanges = fieldsToCompare.some(
            (field) =>
              normalizeValue(field, (currentDriver as any)[field]) !==
              normalizeValue(field, (originalDriver as any)[field])
          );
          if (hasChanges) {
            try {
              await BookingApi.archiveCustomer(resolvedCustomerId);
            } catch (error) {
              console.error("Failed to archive foreigner driver:", error);
            }
            const newDriverResponse = await BookingApi.updateSpecificDriver(
              reservationDetails.reservationId,
              driverNumber + 1,
              currentDriver,
              country
            );
            const newCustomerId =
              (newDriverResponse as any)?.data?.customerId ||
              (newDriverResponse as any)?.customerId;
            if (newCustomerId) {
              updatedDrivers[driverNumber] = {
                ...updatedDrivers[driverNumber],
                customerId: newCustomerId,
              };
            }
          } else {
            // No changes — just update in-place
            const {
              issueBy,
              identificationDocuments,
              drivingLicenseDocuments,
              ...cleanedDriver
            } = currentDriver;
            await BookingApi.updateCustomerDetails(resolvedCustomerId, {
              ...cleanedDriver,
              phNum1:
                cleanedDriver.telephone || (cleanedDriver as any).phNum1 || "",
              issuedBy: issueBy || (cleanedDriver as any).issuedBy || "",
            });
            const idFront = identificationDocuments?.[0];
            const idBack = identificationDocuments?.[1];
            const driverLicenseFront = drivingLicenseDocuments?.[0];
            const driverLicenseBack = drivingLicenseDocuments?.[1];
            if (idFront || idBack || driverLicenseFront || driverLicenseBack) {
              BookingApi.addDocuments(
                parseInt(resolvedCustomerId),
                [idFront, idBack].filter(Boolean) as File[],
                [driverLicenseFront, driverLicenseBack].filter(
                  Boolean
                ) as File[]
              ).catch((err) =>
                console.error("Background document upload failed:", err)
              );
            }
          }
        } else if (
          resolvedCustomerId &&
          (!ec || selectedProfile === "existing")
        ) {
          // Italian: update existing customer record in-place
          const {
            issueBy,
            identificationDocuments,
            drivingLicenseDocuments,
            ...cleanedDriver
          } = currentDriver;

          await BookingApi.updateCustomerDetails(resolvedCustomerId, {
            ...cleanedDriver,
            phNum1:
              cleanedDriver.telephone || (cleanedDriver as any).phNum1 || "",
            issuedBy: issueBy || (cleanedDriver as any).issuedBy || "",
          });

          const idFront = identificationDocuments?.[0];
          const idBack = identificationDocuments?.[1];
          const driverLicenseFront = drivingLicenseDocuments?.[0];
          const driverLicenseBack = drivingLicenseDocuments?.[1];

          if (idFront || idBack || driverLicenseFront || driverLicenseBack) {
            BookingApi.addDocuments(
              parseInt(resolvedCustomerId),
              [idFront, idBack].filter(Boolean) as File[],
              [driverLicenseFront, driverLicenseBack].filter(Boolean) as File[]
            ).catch((err) =>
              console.error("Background document upload failed:", err)
            );
          }
        } else {
          // New profile: create new driver and link to reservation slot (1-based index)
          const newDriverResponse = await BookingApi.updateSpecificDriver(
            reservationDetails.reservationId,
            driverNumber + 1,
            currentDriver,
            country
          );
          const newCustomerId =
            (newDriverResponse as any)?.data?.customerId ||
            (newDriverResponse as any)?.customerId;
          if (newCustomerId) {
            updatedDrivers[driverNumber] = {
              ...updatedDrivers[driverNumber],
              customerId: newCustomerId,
            };
          }
        }

        if (isMountedRef.current) setIsLoading(false);
        onNext(updatedDrivers);
      } catch (error) {
        console.error("Failed to update customer details:", error);
        if (isMountedRef.current) setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    } else {
      onBack();
    }
  };

  const watchedValues = useWatch({
    control,
    name: [
      "firstName",
      "lastName",
      "birthNation",
      "birthPlace",
      "sex",
      "dateOfBirth",
      "country",
      "city",
    ],
  });

  const [
    wFirstName,
    wLastName,
    wBirthNation,
    wBirthPlace,
    wSex,
    wDateOfBirth,
    wCountry,
    wCity,
  ] = watchedValues;

  useEffect(() => {
    if (ssnDebounceRef.current) {
      clearTimeout(ssnDebounceRef.current);
    }

    const allFieldsFilled =
      wFirstName &&
      wLastName &&
      wBirthNation &&
      wBirthPlace &&
      wSex &&
      wDateOfBirth &&
      wCountry &&
      wCity;

    // Immediately clear SSN on any change so user sees instant feedback
    if (
      allFieldsFilled &&
      wCountry?.toUpperCase().startsWith("ITAL") &&
      isMountedRef.current
    ) {
      setValue("ssnNumber", "", { shouldValidate: false });
    }

    ssnDebounceRef.current = setTimeout(async () => {
      if (allFieldsFilled) {
        const selectedCountry = wCountry?.toUpperCase();
        if (selectedCountry.startsWith("ITAL")) {
          try {
            const response = await BookingApi.getSsnByCustomerDetails(
              wFirstName,
              wLastName,
              wBirthNation,
              wBirthPlace,
              wSex,
              wDateOfBirth as any,
              wCountry
            );
            if (isMountedRef.current) {
              const ssnValue = String(response?.data ?? "");
              setValue(
                "ssnNumber",
                ssnValue === "100000000000000000"
                  ? "9999999999999999"
                  : ssnValue,
                { shouldValidate: true }
              );
            }
          } catch (error) {
            console.error("Error fetching SSN:", error);
          }
        } else if (isMountedRef.current) {
          setValue("ssnNumber", "9999999999999999", {
            shouldValidate: true,
          });
        }
      }
    }, 500);
    return () => {
      if (ssnDebounceRef.current) {
        clearTimeout(ssnDebounceRef.current);
      }
    };
  }, [
    wFirstName,
    wLastName,
    wBirthNation,
    wBirthPlace,
    wSex,
    wDateOfBirth,
    wCountry,
    wCity,
    setValue,
    countries,
  ]);

  const requiredConfig = {
    phoneNumber: appSettings?.driverPhoneNoRequired,
    email: appSettings?.driverEmailRequired,
    streetName: appSettings?.driverStreetNameRequired,
    streetNumber: appSettings?.driverStreetNoRequired,
    country: appSettings?.driverCountryRequired,
    state: appSettings?.driverStateRequired,
    city: appSettings?.driverCityRequired,
    zipCode: appSettings?.driverZipCodeRequired,
    birthDate: appSettings?.driverBirthDateRequired,
    birthNation: appSettings?.driverBirthNationRequired,
    birthPlace: appSettings?.driverBirthPlaceRequired,
    birthProvince: appSettings?.driverBirthProvRequired,
    sex: appSettings?.driverGenderRequired,
    ssnNumber: appSettings?.driverSSNRequired,
    document: appSettings?.driverDocumentRequired,
    licenseType: appSettings?.driverLicenceTypeRequired,
    releaseDate: appSettings?.driverReleaseDateRequired,
    documentExpiryDate: appSettings?.driverExpiryDateRequired,
    issueBy: appSettings?.driverIssueByRequired,
  };

  const prefixFilterOptions = createFilterOptions<string>({
    limit: 50,
    matchFrom: "start",
  });
  const getRules = (fieldName: string, label: string) => {
    return requiredConfig[fieldName]
      ? { required: `${translate(label)} ${translate("is required")}` }
      : {};
  };

  const getDateRules = (fieldName: string, label: string) => ({
    ...getRules(fieldName, label),
    validate: (value: any) => {
      if (!value) return true;
      const d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return translate("Invalid date");
      if (d.getFullYear() > 3000) return translate("Invalid date");
      return true;
    },
  });

  const [birthNation, birthPlace] = useWatch({
    control,
    name: ["birthNation", "birthPlace"],
  });

  const [selectedCountry, selectedCity] = useWatch({
    control,
    name: ["country", "city"],
  });

  const selectedBirthNations = useWatch({ control, name: "birthNation" });

  const showDropDownItalyAddress =
    selectedCountrys?.toUpperCase() === "ITALIA" ||
    selectedCountrys?.toUpperCase() === "ITALY";

  const showDropDownItalyBirth =
    selectedBirthNations?.toUpperCase() === "ITALIA" ||
    selectedBirthNations?.toUpperCase() === "ITALY";

  const [prevCountry, setPrevCountry] = useState(selectedCountrys);
  const [prevBirthNation, setPrevBirthNation] = useState(selectedBirthNations);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (selectedCountry) {
          const response = await BookingApi.getStates(selectedCountry, country);
          if (isMountedRef.current) {
            setCities(response.data || []);
          }

          // Clear city, state, and zip values when switching between Italy and non-Italy
          const wasItaly =
            prevCountry &&
            (prevCountry.toUpperCase() === "ITALIA" ||
              prevCountry.toUpperCase() === "ITALY");
          const isItaly = showDropDownItalyAddress;

          if (prevCountry && wasItaly !== isItaly && isMountedRef.current) {
            setValue("city", "");
            setValue("state", "");
            setValue("zip", "");
          }
          if (isMountedRef.current) {
            setPrevCountry(selectedCountry);
          }
        } else if (isMountedRef.current) {
          setCities([]);
        }

        if (selectedCity && showDropDownItalyAddress) {
          const response = await BookingApi.getCity(selectedCity);
          if (isMountedRef.current) {
            setStates(response.data.state || "");
            setZipCode(response.data.zip || "");
          }
        } else if (isMountedRef.current) {
          setStates([]);
          setZipCode([]);
        }
      } catch (err) {
        console.error("Error fetching location data:", err);
      }
    };

    fetchData();
  }, [
    selectedCountry,
    selectedCity,
    country,
    showDropDownItalyAddress,
    setValue,
    prevCountry,
  ]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (birthNation) {
          const response = await BookingApi.getStates(birthNation, country);
          if (isMountedRef.current) {
            setBirthCities(response.data || []);
          }

          // Clear birth place and province when switching between Italy and non-Italy
          const wasItaly =
            prevBirthNation &&
            (prevBirthNation.toUpperCase() === "ITALIA" ||
              prevBirthNation.toUpperCase() === "ITALY");
          const isItaly = showDropDownItalyBirth;

          if (prevBirthNation && wasItaly !== isItaly && isMountedRef.current) {
            setValue("birthPlace", "");
            setValue("birthProvince", "");
          }
          if (isMountedRef.current) {
            setPrevBirthNation(birthNation);
          }
        } else if (isMountedRef.current) {
          setBirthCities([]);
        }

        if (birthPlace && showDropDownItalyBirth) {
          const response = await BookingApi.getCity(birthPlace);
          if (isMountedRef.current) {
            setBirthStates(response.data.state || "");
          }
        } else if (isMountedRef.current) {
          setBirthStates([]);
        }
      } catch (err) {
        console.error("Error fetching location data:", err);
      }
    };

    fetchData();
  }, [
    birthNation,
    birthPlace,
    country,
    showDropDownItalyBirth,
    setValue,
    prevBirthNation,
  ]);

  const reverseDocumentTypeMap = React.useMemo(
    () =>
      Object.entries(documentTypeMap).reduce((acc, [key, value]) => {
        acc[value] = key;
        return acc;
      }, {} as Record<string, string>),
    [documentTypeMap]
  );

  const fallbackDocumentTypes = [
    "PATENTE",
    "CARTA D'IDENTITA'",
    "PASSAPORTO",
    "CARTA ID. DIPLOMATICA",
    "CARTA IDENTITA' ELETTRONICA",
    "CARTA DI IDENTITA'",
    "CI",
    "PASSAPORTO DIPLOMATICO",
    "PASSAPORTO ORDINARIO",
    "PASSAPORTO DI SERVIZIO",
  ];

  const filteredDocumentTypes = (
    Array.isArray(documentTypes) && documentTypes.length > 0
      ? documentTypes
      : fallbackDocumentTypes
  ).filter((docType: string) => docType !== "PATENTE DI GUIDA");

  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  // Render section 1: Personal Information
  const renderSection1 = () => (
    <>
      <Controller
        name="firstName"
        control={control}
        defaultValue=""
        rules={{
          required: `${translate("First Name")} ${translate("is required")}`,
        }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("First Name")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              className={`webCheckInTextField ${
                isInvoiceAlreadyCreated ? "readOnlyField" : ""
              }`}
              InputLabelProps={{ shrink: false }}
              inputProps={{
                readOnly: isInvoiceAlreadyCreated,
                style: enableUppercaseInputField
                  ? { textTransform: "uppercase" }
                  : {},
              }}
              onChange={(e) => {
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                );
                trigger("firstName");
              }}
            />
          </>
        )}
      />

      <Controller
        name="lastName"
        control={control}
        defaultValue=""
        rules={{
          required: `${translate("Last Name")} ${translate("is required")}`,
        }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Last Name")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              className={`webCheckInTextField ${
                isInvoiceAlreadyCreated ? "readOnlyField" : ""
              }`}
              InputLabelProps={{ shrink: false }}
              inputProps={{
                readOnly: isInvoiceAlreadyCreated,
                style: enableUppercaseInputField
                  ? { textTransform: "uppercase" }
                  : {},
              }}
              onChange={(e) => {
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                );
                trigger("lastName");
              }}
            />
          </>
        )}
      />

      <Divider className="webCheckInDivider" />

      <Controller
        name="telephone"
        control={control}
        rules={{
          required: `${translate("Phone Number")} ${translate("is required")}`,
          validate: (value) => {
            const digits = (value || "").replace(/\D/g, "");
            return (
              digits.length >= 5 ||
              `${translate("Phone Number")} ${translate("is required")}`
            );
          },
        }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Phone Number")}
            </Typography>
            <RHFPhoneInput
              {...field}
              country="it"
              onChange={(val: string) => {
                field.onChange(val);
                trigger("telephone");
              }}
            />
            {errors.telephone && (
              <Typography variant="caption" color="error">
                {errors.telephone.message}
              </Typography>
            )}
          </>
        )}
      />

      <Controller
        name="email"
        control={control}
        defaultValue=""
        rules={{
          required: `${translate("Email")} ${translate("is required")}`,
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: translate("Email is invalid"),
          },
        }}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Email")}
            </Typography>
            <TextField
              {...field}
              type="email"
              fullWidth
              error={!!errors.email}
              helperText={errors.email?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) => {
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                );
                trigger("email");
              }}
            />
          </>
        )}
      />
    </>
  );

  // Render section 2: Address Information
  const renderSection2 = () => {
    const uniqueCountries = Array.from(new Set(countries || []));

    return (
      <>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Controller
              name="streetName"
              control={control}
              defaultValue=""
              rules={getRules("streetName", "Address")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Address")}
                  </Typography>
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.streetName}
                    helperText={errors.streetName?.message}
                    className="webCheckInTextField"
                    InputLabelProps={{ shrink: false }}
                    inputProps={
                      enableUppercaseInputField
                        ? { style: { textTransform: "uppercase" } }
                        : {}
                    }
                    onChange={(e) =>
                      field.onChange(
                        enableUppercaseInputField
                          ? e.target.value.toUpperCase()
                          : e.target.value
                      )
                    }
                  />
                </>
              )}
            />
          </Grid>
          <Grid item xs={4}>
            <Controller
              name="streetNumber"
              control={control}
              defaultValue=""
              rules={getRules("streetNumber", "N")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("N")}
                  </Typography>
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.streetNumber}
                    helperText={errors.streetNumber?.message}
                    className="webCheckInTextField"
                    InputLabelProps={{ shrink: false }}
                    FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                    inputProps={
                      enableUppercaseInputField
                        ? { style: { textTransform: "uppercase" } }
                        : {}
                    }
                    onChange={(e) =>
                      field.onChange(
                        enableUppercaseInputField
                          ? e.target.value.toUpperCase()
                          : e.target.value
                      )
                    }
                  />
                </>
              )}
            />
          </Grid>
        </Grid>

        <Controller
          name="country"
          control={control}
          defaultValue=""
          rules={getRules("country", "Country")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Country")}
              </Typography>
              <div
                className={`webCheckInTextField ${
                  isInvoiceAlreadyCreated ? "readOnlyField" : ""
                }`}
              >
                <AppCountrySelect
                  fieldKey="country"
                  label=""
                  value={field.value || ""}
                  options={uniqueCountries}
                  required={!!requiredConfig.country}
                  readOnly={isInvoiceAlreadyCreated}
                  translate={translate}
                  formStates={{
                    setValue: (key: string, val: any, opts?: any) => {
                      setValue(key, val, { shouldValidate: true, ...opts });
                      if (key === "country") field.onChange(val);
                    },
                  }}
                  setUserDetails={() => {}}
                  onChange={(val: string) => {
                    field.onChange(val);
                  }}
                  cityFieldName="city"
                  stateFieldName="state"
                  zipFieldName="zip"
                  enableUppercaseInputField={enableUppercaseInputField}
                />
              </div>
              <FormHelperText error>{errors.country?.message}</FormHelperText>
            </>
          )}
        />

        <Controller
          name="city"
          defaultValue=""
          control={control}
          rules={getRules("city", "City")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("City")}
              </Typography>
              {showDropDownItalyAddress ? (
                <Autocomplete
                  {...field}
                  options={cities}
                  value={field.value || ""}
                  disableClearable
                  filterOptions={(options, state) => {
                    const effectiveInput =
                      state.inputValue || field.value || "";
                    return prefixFilterOptions(options, {
                      ...state,
                      inputValue: effectiveInput,
                    });
                  }}
                  openOnFocus
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={async (_, value) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    );
                  }}
                  onInputChange={(_, value) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    );
                  }}
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      // className="webCheckInTextField"
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      inputProps={{
                        ...params.inputProps,
                        style: enableUppercaseInputField
                          ? { textTransform: "uppercase" }
                          : {},
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.city}
                  helperText={errors.city?.message}
                  className="webCheckInTextField"
                  inputProps={
                    enableUppercaseInputField
                      ? { style: { textTransform: "uppercase" } }
                      : {}
                  }
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              )}
            </>
          )}
        />

        <Grid container spacing={2}>
          <Grid item xs={isSmallScreen ? 5.5 : 7}>
            <Controller
              name="state"
              defaultValue=""
              control={control}
              rules={getRules("state", "State")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("State")}
                  </Typography>
                  {showDropDownItalyAddress ? (
                    <Autocomplete
                      {...field}
                      options={states}
                      value={field.value || ""}
                      disableClearable
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : String(option)
                      }
                      onChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      onInputChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      disabled={isInvoiceAlreadyCreated}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.state}
                          helperText={errors.state?.message}
                          // className="webCheckInTextField"
                          className={`webCheckInTextField ${
                            isInvoiceAlreadyCreated ? "readOnlyField" : ""
                          }`}
                          inputProps={{
                            ...params.inputProps,
                            style: enableUppercaseInputField
                              ? { textTransform: "uppercase" }
                              : {},
                          }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.state}
                      helperText={errors.state?.message}
                      className="webCheckInTextField"
                      inputProps={
                        enableUppercaseInputField
                          ? { style: { textTransform: "uppercase" } }
                          : {}
                      }
                      onChange={(e) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? e.target.value.toUpperCase()
                            : e.target.value
                        )
                      }
                    />
                  )}
                </>
              )}
            />
          </Grid>
          <Grid item xs={isSmallScreen ? 6.3 : 5}>
            <Controller
              name="zip"
              defaultValue=""
              control={control}
              rules={getRules("zipCode", "Zip Code")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Zip Code")}
                  </Typography>
                  {showDropDownItalyAddress ? (
                    <Autocomplete
                      {...field}
                      options={zipCode}
                      value={field.value || ""}
                      disableClearable
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : String(option)
                      }
                      onChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      onInputChange={(_, value) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? (value || "").toUpperCase()
                            : value
                        )
                      }
                      disabled={isInvoiceAlreadyCreated}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.zip}
                          helperText={errors.zip?.message}
                          // className="webCheckInTextField"
                          className={`webCheckInTextField ${
                            isInvoiceAlreadyCreated ? "readOnlyField" : ""
                          }`}
                          FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                          inputProps={{
                            ...params.inputProps,
                            style: enableUppercaseInputField
                              ? { textTransform: "uppercase" }
                              : {},
                          }}
                        />
                      )}
                    />
                  ) : (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.zip}
                      helperText={errors.zip?.message}
                      className="webCheckInTextField"
                      // inputProps={{
                      //   inputMode: "numeric",
                      //   pattern: "[0-9]*",
                      //   message: translate("Only numbers are allowed"),
                      // }}
                      // onChange={(e) => {
                      //   const onlyNumbers = e.target.value.replace(/\D/g, "");
                      //   field.onChange(onlyNumbers);
                      // }}
                      FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                      inputProps={
                        enableUppercaseInputField
                          ? { style: { textTransform: "uppercase" } }
                          : {}
                      }
                      onChange={(e) =>
                        field.onChange(
                          enableUppercaseInputField
                            ? e.target.value.toUpperCase()
                            : e.target.value
                        )
                      }
                    />
                  )}
                </>
              )}
            />
          </Grid>
        </Grid>
      </>
    );
  };

  // Render section 3: Birth Information
  const renderSection3 = () => {
    const uniqueCountries = Array.from(new Set(countries));
    return (
      <>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={dateLocale}
          localeText={muiLocaleText}
        >
          <Controller
            name="dateOfBirth"
            control={control}
            defaultValue=""
            rules={getDateRules("birthDate", "Date of birth")}
            render={({ field }) => (
              <>
                <Typography variant="body2" className="webCheckInText">
                  {translate("Date of birth")}
                </Typography>
                <DesktopDatePicker
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  inputFormat="dd/MM/yyyy"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      error={!!errors.dateOfBirth}
                      helperText={errors.dateOfBirth?.message}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: "",
                        readOnly: isInvoiceAlreadyCreated,
                      }}
                    />
                  )}
                />
              </>
            )}
          />
        </LocalizationProvider>

        <Controller
          name="birthNation"
          control={control}
          defaultValue=""
          rules={getRules("birthNation", "Country of birth")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Country of birth")}
              </Typography>
              <div
                className={`webCheckInTextField ${
                  isInvoiceAlreadyCreated ? "readOnlyField" : ""
                }`}
              >
                <AppCountrySelect
                  fieldKey="birthNation"
                  label=""
                  value={field.value || ""}
                  options={uniqueCountries}
                  required={!!requiredConfig.birthNation}
                  readOnly={isInvoiceAlreadyCreated}
                  translate={translate}
                  formStates={{
                    setValue: (key: string, val: any, opts?: any) => {
                      setValue(key, val, { shouldValidate: true, ...opts });
                      if (key === "birthNation") field.onChange(val);
                    },
                  }}
                  setUserDetails={() => {}}
                  onChange={(val: string) => {
                    field.onChange(val);
                  }}
                  cityFieldName="birthPlace"
                  stateFieldName="birthProvince"
                  enableUppercaseInputField={enableUppercaseInputField}
                />
              </div>
              <FormHelperText error>
                {errors.birthNation?.message}
              </FormHelperText>
            </>
          )}
        />

        <Controller
          name="birthPlace"
          defaultValue=""
          control={control}
          rules={getRules("birthPlace", "Place of birth")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Place of birth")}
              </Typography>
              {showDropDownItalyBirth ? (
                <Autocomplete
                  {...field}
                  options={birthCities}
                  value={field.value || ""}
                  disableClearable
                  filterOptions={prefixFilterOptions}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={async (_, value) => {
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    );
                  }}
                  onInputChange={(_, value) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    )
                  }
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.birthPlace}
                      helperText={errors.birthPlace?.message}
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      inputProps={{
                        ...params.inputProps,
                        style: enableUppercaseInputField
                          ? { textTransform: "uppercase" }
                          : {},
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.birthPlace}
                  helperText={errors.birthPlace?.message}
                  className="webCheckInTextField"
                  inputProps={
                    enableUppercaseInputField
                      ? { style: { textTransform: "uppercase" } }
                      : {}
                  }
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              )}
            </>
          )}
        />

        <Controller
          name="birthProvince"
          defaultValue=""
          control={control}
          rules={getRules("birthProvince", "State of birth")}
          render={({ field }) => (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("State of birth")}
              </Typography>
              {showDropDownItalyBirth ? (
                <Autocomplete
                  {...field}
                  options={birthStates}
                  value={field.value || ""}
                  disableClearable
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : String(option)
                  }
                  isOptionEqualToValue={(option, value) => option === value}
                  onChange={(_, value) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    )
                  }
                  onInputChange={(_, value) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? (value || "").toUpperCase()
                        : value
                    )
                  }
                  disabled={isInvoiceAlreadyCreated}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.birthProvince}
                      helperText={errors.birthProvince?.message}
                      className={`webCheckInTextField ${
                        isInvoiceAlreadyCreated ? "readOnlyField" : ""
                      }`}
                      inputProps={{
                        ...params.inputProps,
                        style: enableUppercaseInputField
                          ? { textTransform: "uppercase" }
                          : {},
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  {...field}
                  fullWidth
                  error={!!errors.birthProvince}
                  helperText={errors.birthProvince?.message}
                  className="webCheckInTextField"
                  inputProps={
                    enableUppercaseInputField
                      ? { style: { textTransform: "uppercase" } }
                      : {}
                  }
                  onChange={(e) =>
                    field.onChange(
                      enableUppercaseInputField
                        ? e.target.value.toUpperCase()
                        : e.target.value
                    )
                  }
                />
              )}
            </>
          )}
        />
      </>
    );
  };

  // Render section 4: Gender Information
  const renderSection4 = () => (
    <>
      <Controller
        name="sex"
        control={control}
        defaultValue=""
        rules={getRules("sex", "Gender")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Gender")}
            </Typography>
            <RadioGroup
              row
              {...field}
              onChange={(e) => field.onChange(e.target.value)}
              value={field.value}
              sx={webCheckInRadioGroup}
            >
              <FormControlLabel
                value="FEMALE"
                control={
                  <Radio
                    sx={{
                      "&.Mui-checked": {
                        color: isInvoiceAlreadyCreated
                          ? "readOnlyField"
                          : webCheckinSecondaryColor || "#ff238c",
                      },
                    }}
                  />
                }
                label={translate("Female")}
                disabled={isInvoiceAlreadyCreated}
              />
              <FormControlLabel
                value="MALE"
                control={
                  <Radio
                    sx={{
                      "&.Mui-checked": {
                        color: isInvoiceAlreadyCreated
                          ? "readOnlyField"
                          : webCheckinSecondaryColor || "#ff238c",
                      },
                    }}
                  />
                }
                label={translate("Male")}
                disabled={isInvoiceAlreadyCreated}
              />
            </RadioGroup>
            {errors.sex && (
              <FormHelperText error>{errors.sex.message}</FormHelperText>
            )}
          </>
        )}
      />
      <Divider className="webCheckInDivider" />
      <Controller
        name="ssnNumber"
        control={control}
        defaultValue=""
        rules={getRules("ssnNumber", "SSN1")}
        render={({ field }) => {
          return (
            <>
              <Typography variant="body2" className="webCheckInText">
                {translate("Tax Identification Number (TIN)")}
              </Typography>
              <TextField
                {...field}
                fullWidth
                error={!!errors.ssnNumber}
                helperText={errors.ssnNumber?.message}
                InputLabelProps={{ shrink: false }}
                className={`webCheckInTextField ${
                  isInvoiceAlreadyCreated ? "readOnlyField" : ""
                }`}
                disabled={isNotItalian}
                inputProps={{
                  readOnly: isNotItalian || isInvoiceAlreadyCreated,
                  style: enableUppercaseInputField
                    ? { textTransform: "uppercase" }
                    : {},
                }}
                onChange={(e) =>
                  field.onChange(
                    enableUppercaseInputField
                      ? e.target.value.toUpperCase()
                      : e.target.value
                  )
                }
              />
            </>
          );
        }}
      />
    </>
  );

  // Render section 5: Drive Licence
  const renderSection5 = () => (
    <>
      <Controller
        name="document"
        control={control}
        defaultValue="DRIVER'S LICENSE"
        rules={{}}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Type of Document")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              value={
                isShareboxEnabled
                  ? translate("DRIVER'S LICENSE")
                  : "DRIVER'S LICENSE"
              }
              InputProps={{ readOnly: true }}
              className="webCheckInTextField"
            />
          </>
        )}
      />

      <Controller
        name="documentNumber"
        control={control}
        defaultValue=""
        rules={getRules("document", "Document Number")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Document Number")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.documentNumber}
              helperText={errors.documentNumber?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />

      <Controller
        name="licenseType"
        control={control}
        defaultValue=""
        rules={getRules("licenseType", "License Type")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("License Type")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.licenseType}
              helperText={errors.licenseType?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />

      <Grid container spacing={2}>
        <Grid item xs={5.5}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="releaseDate"
              control={control}
              defaultValue=""
              rules={getDateRules("releaseDate", "Release Date")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Release Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.releaseDate}
                        helperText={errors.releaseDate?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                        FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={6.3}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="documentExpiryDate"
              control={control}
              defaultValue=""
              rules={getDateRules("documentExpiryDate", "Expiry Date")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Expiry Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.documentExpiryDate}
                        helperText={errors.documentExpiryDate?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                        FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>

      <Controller
        name="issueBy"
        control={control}
        defaultValue=""
        rules={getRules("issueBy", "Release by")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Release by")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.issueBy}
              helperText={errors.issueBy?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />
    </>
  );

  // Render section 6: ID / Passport
  const renderSection6 = () => (
    <>
      <Controller
        name="document1"
        control={control}
        defaultValue=""
        rules={getRules("document", "Type of Document")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Type of Document")}
            </Typography>
            <TextField
              {...field}
              select
              fullWidth
              error={!!errors.document1}
              helperText={errors.document1?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              value={documentTypeMap[field.value] || field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
              }}
            >
              {filteredDocumentTypes.map((docType: string, index: number) => (
                <MenuItem key={index} value={docType}>
                  {docType}
                </MenuItem>
              ))}
            </TextField>
          </>
        )}
      />

      <Controller
        name="documentNumb1"
        control={control}
        defaultValue=""
        rules={getRules("document", "Document Number")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Document Number")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.documentNumb1}
              helperText={errors.documentNumb1?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />

      <Grid container spacing={2}>
        <Grid item xs={5.5}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="releaseDate1"
              control={control}
              defaultValue=""
              rules={getDateRules("releaseDate", "Release Date")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Release Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.releaseDate1}
                        helperText={errors.releaseDate1?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                        FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6.3}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={dateLocale}
            localeText={muiLocaleText}
          >
            <Controller
              name="expiryDate1"
              control={control}
              defaultValue=""
              rules={getDateRules("documentExpiryDate", "Expiry Date")}
              render={({ field }) => (
                <>
                  <Typography variant="body2" className="webCheckInText">
                    {translate("Expiry Date")}
                  </Typography>
                  <DesktopDatePicker
                    value={field.value}
                    onChange={(newValue) => field.onChange(newValue)}
                    inputFormat="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        className="webCheckInTextField"
                        error={!!errors.expiryDate1}
                        helperText={errors.expiryDate1?.message}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: "",
                        }}
                        FormHelperTextProps={{ sx: { whiteSpace: "nowrap" } }}
                      />
                    )}
                  />
                </>
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      <Controller
        name="issueBy1"
        control={control}
        defaultValue=""
        rules={getRules("issueBy", "Release by")}
        render={({ field }) => (
          <>
            <Typography variant="body2" className="webCheckInText">
              {translate("Release by")}
            </Typography>
            <TextField
              {...field}
              fullWidth
              error={!!errors.issueBy1}
              helperText={errors.issueBy1?.message}
              className="webCheckInTextField"
              InputLabelProps={{ shrink: false }}
              inputProps={
                enableUppercaseInputField
                  ? { style: { textTransform: "uppercase" } }
                  : {}
              }
              onChange={(e) =>
                field.onChange(
                  enableUppercaseInputField
                    ? e.target.value.toUpperCase()
                    : e.target.value
                )
              }
            />
          </>
        )}
      />
    </>
  );

  const renderSection7 = () => {
    const isNewSelected = selectedProfile === "new";
    const isExistingSelected = selectedProfile === "existing";
    const formValues = watch();
    const cardStyle = (isSelected: boolean) => ({
      border: isSelected
        ? `2px solid ${webCheckinHeaderBgColor || "#2cb133"}`
        : "2px solid #e8e8e8",
      borderRadius: "16px",
      padding: isMobileOrTablet ? "12px" : "20px",
      cursor: "pointer",
      height: "100%",
      display: "flex",
      flexDirection: "column" as const,
      transition: "all 0.25s ease",
      backgroundColor: "#fff",
      boxShadow: isSelected
        ? "0 4px 20px rgba(0,188,212,0.18)"
        : "0 2px 8px rgba(0,0,0,0.06)",
    });

    const FieldRow = ({
      label,
      value,
      greyed,
    }: {
      label: string;
      value: string | React.ReactNode;
      greyed: boolean;
    }) => (
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: greyed ? "#ccc" : "#888",
            fontWeight: 600,
            fontSize: "10px",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            display: "block",
            mb: 0.2,
          }}
        >
          {label}
        </Typography>
        {typeof value === "string" ? (
          <Typography
            variant="body2"
            sx={{
              color: greyed ? "#bbb" : "#1a1a1a",
              fontWeight: greyed ? 400 : 600,
              fontSize: "13px",
            }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
        <Divider sx={{ mt: 1, borderColor: greyed ? "#f0f0f0" : "#ebebeb" }} />
      </Box>
    );

    return (
      <Box sx={{ mx: -3, mt: -2 }}>
        <Box sx={{ backgroundColor: "#fff", p: { xs: 1, sm: 2 } }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            {/* NEW DETAILS card */}
            <Grid item xs={12} md={6}>
              <Box
                sx={cardStyle(isNewSelected)}
                onClick={() => setSelectedProfile("new")}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <AccountCircleIcon
                    sx={{
                      mr: 0.8,
                      color: isNewSelected ? "#1a1a1a" : "#ccc",
                      fontSize: 22,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      fontSize: "13px",
                      letterSpacing: 0.5,
                      color: isNewSelected ? "#1a1a1a" : "#bbb",
                    }}
                  >
                    {translate("NEW DETAILS")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <FieldRow
                  label={translate("Driver Name")}
                  value={`${formValues.firstName || ""} ${
                    formValues.lastName || ""
                  }`.trim()}
                  greyed={!isNewSelected}
                />
                <FieldRow
                  label={translate("Phone Number")}
                  value={formValues.telephone || ""}
                  greyed={!isNewSelected}
                />
                <FieldRow
                  label={translate("Mail")}
                  value={formValues.email || ""}
                  greyed={!isNewSelected}
                />
                <FieldRow
                  label={translate("Address")}
                  greyed={!isNewSelected}
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        color: isNewSelected ? "#1a1a1a" : "#bbb",
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                    >
                      {[
                        formValues.streetName,
                        formValues.streetNumber,
                        formValues.zip,
                        formValues.city,
                        formValues.state,
                        formValues.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  }
                />
                <FieldRow
                  label={translate("Date of Birth")}
                  value={
                    formValues.dateOfBirth
                      ? typeof formValues.dateOfBirth === "string"
                        ? formValues.dateOfBirth.split("T")[0]
                        : new Date(formValues.dateOfBirth)
                            .toISOString()
                            .split("T")[0]
                      : ""
                  }
                  greyed={!isNewSelected}
                />
                <FieldRow
                  label={translate("Place of Birth")}
                  greyed={!isNewSelected}
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        color: isNewSelected ? "#1a1a1a" : "#bbb",
                        fontWeight: 600,
                        fontSize: "13px",
                      }}
                    >
                      {[
                        formValues.birthPlace,
                        formValues.birthProvince,
                        formValues.birthNation,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobileOrTablet ? "center" : "flex-end",
                    mt: "auto",
                    pt: 1.5,
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: isMobileOrTablet ? "center" : "right",
                      color: "#555",
                      fontSize: "10px",
                      lineHeight: 1.3,
                    }}
                  >
                    {translate("Select this profile")}
                  </Typography>
                  <Radio
                    checked={isNewSelected}
                    onChange={() => setSelectedProfile("new")}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: webCheckinHeaderBgColor || "#2cb133",
                      "&.Mui-checked": {
                        color: webCheckinHeaderBgColor || "#2cb133",
                      },
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* EXISTING DETAILS card */}
            <Grid item xs={12} md={6}>
              <Box
                sx={cardStyle(isExistingSelected)}
                onClick={() => setSelectedProfile("existing")}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <AccountCircleIcon
                    sx={{
                      mr: 0.8,
                      color: isExistingSelected ? "#1a1a1a" : "#ccc",
                      fontSize: 22,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      fontSize: "13px",
                      letterSpacing: 0.5,
                      color: isExistingSelected ? "#1a1a1a" : "#bbb",
                    }}
                  >
                    {translate("EXISTING DETAILS")}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5, borderColor: "#f0f0f0" }} />
                <FieldRow
                  label={translate("Driver Name")}
                  value={`${existingDriverData?.customers?.firstName || ""} ${
                    existingDriverData?.customers?.lastName || ""
                  }`.trim()}
                  greyed={!isExistingSelected}
                />
                <FieldRow
                  label={translate("Phone Number")}
                  value={
                    existingDriverData?.customers?.telephone ||
                    existingDriverData?.customers?.phoneNumber ||
                    existingDriverData?.customers?.mobile ||
                    ""
                  }
                  greyed={!isExistingSelected}
                />
                <FieldRow
                  label={translate("Mail")}
                  value={
                    existingDriverData?.customers?.email ||
                    existingDriverData?.customers?.email_invoice ||
                    ""
                  }
                  greyed={!isExistingSelected}
                />
                <FieldRow
                  label={translate("Address")}
                  greyed={!isExistingSelected}
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        color: isExistingSelected ? "#1a1a1a" : "#bbb",
                        fontSize: "13px",
                      }}
                    >
                      {[
                        existingDriverData?.customers?.street,
                        existingDriverData?.customers?.addressNumber ||
                          existingDriverData?.customers?.n,
                        existingDriverData?.customers?.postalCode,
                        existingDriverData?.customers?.city,
                        existingDriverData?.customers?.province ||
                          existingDriverData?.customers?.state,
                        existingDriverData?.customers?.country,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  }
                />
                <FieldRow
                  label={translate("Date of Birth")}
                  value={
                    existingDriverData?.customers?.dob
                      ? existingDriverData.customers.dob.split("T")[0]
                      : ""
                  }
                  greyed={!isExistingSelected}
                />
                <FieldRow
                  label={translate("Place of Birth")}
                  greyed={!isExistingSelected}
                  value={
                    <Typography
                      variant="body2"
                      sx={{
                        color: isExistingSelected ? "#1a1a1a" : "#bbb",
                        fontSize: "13px",
                      }}
                    >
                      {[
                        existingDriverData?.customers?.birthplace,
                        existingDriverData?.customers?.birthProv,
                        existingDriverData?.customers?.birthNation,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </Typography>
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isMobileOrTablet ? "center" : "flex-end",
                    mt: "auto",
                    pt: 1.5,
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#aaa",
                      fontSize: "10px",
                      textAlign: isMobileOrTablet ? "center" : "right",
                    }}
                  >
                    {translate("Keep this profile")}
                  </Typography>
                  <Radio
                    checked={isExistingSelected}
                    onChange={() => setSelectedProfile("existing")}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: isExistingSelected
                        ? webCheckinHeaderBgColor || "#2cb133"
                        : "#ccc",
                      "&.Mui-checked": {
                        color: webCheckinHeaderBgColor || "#2cb133",
                      },
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  const handleClose = (): void => {
    setOpenModal(false);
  };
  return (
    <>
      <WebCheckInLayout
        title={
          currentSection === 7 && !isNotItalian
            ? translate("SELECT YOUR PROFILE")
            : translate("Driver Details")
        }
        progress={nestedProgress}
        onBack={handleBack}
        wide={currentSection === 7 && !isNotItalian}
      >
        <form onSubmit={handleSubmit(handleContinue)}>
          {currentSection === 1 && renderSection1()}
          {currentSection === 2 && renderSection2()}
          {currentSection === 3 && renderSection3()}
          {currentSection === 4 && renderSection4()}
          {currentSection === 5 && renderSection5()}
          {currentSection === 6 && renderSection6()}
          {currentSection === 7 &&
            !isNotItalian &&
            !!existingDriverData?.customers &&
            renderSection7()}

          <Box textAlign="center">
            <Button
              type="submit"
              variant="contained"
              className="webCheckInButton"
              style={{
                borderRadius: `${appSettings?.buttonBorderRadius || 55}px`,
              }}
              sx={
                isShareboxEnabled
                  ? {
                      width: "100% !important",
                      maxWidth: "none !important",
                      borderRadius: "10px !important",
                      border: `2px solid ${
                        appSettings?.webCheckinButtonColor || "#19344FCC"
                      } !important`,
                    }
                  : undefined
              }
            >
              {currentSection === 7 ||
              (currentSection === 6 && (!existingDriverData || isNotItalian))
                ? translate("Confirm")
                : translate("Continue")}
            </Button>
          </Box>
        </form>
      </WebCheckInLayout>
      {openModal && (
        <WebCheckInPopUp
          openModal={openModal}
          modalMessage={modalMessage}
          onRequestClose={handleClose}
          isSmallScreen={isSmallScreen}
        />
      )}
    </>
  );
}
